import { Button } from "~/components/ui/button";
import { Modal } from "~/components/Modal";
import { SelectInput } from "~/components/SelectInput";
import { SelectOption } from "~/components/SelectInput/types";
import { Checkbox } from "~/components/ui/checkbox";
import { cn } from "~/lib/utils";
import {
    UserProfileAcquisitionChannels,
    UserProfileCompanyTypes,
} from "~/modules/user/store/types";

// // // //

export interface InlineOnboardingFormValues {
    name: string;
    company: string;
    companyType: UserProfileCompanyTypes;
    acquisitionChannel: UserProfileAcquisitionChannels;
    shouldReceiveMarketing: boolean;
}

export interface InlineOnboardingFormErrors {
    name: boolean;
    company: boolean;
    companyType: boolean;
    acquisitionChannel: boolean;
}

export const EMPTY_INLINE_ONBOARDING_FORM_VALUES: InlineOnboardingFormValues = {
    name: "",
    company: "",
    companyType: UserProfileCompanyTypes.None,
    acquisitionChannel: UserProfileAcquisitionChannels.None,
    shouldReceiveMarketing: true,
};

export const EMPTY_INLINE_ONBOARDING_FORM_ERRORS: InlineOnboardingFormErrors = {
    name: false,
    company: false,
    companyType: false,
    acquisitionChannel: false,
};

const COMPANY_TYPE_GROUPS = [
    {
        label: "Buy-Side",
        options: [
            UserProfileCompanyTypes.BuySideIndividualInvestor,
            UserProfileCompanyTypes.BuySideFamilyOffice,
            UserProfileCompanyTypes.BuySideRIA,
            UserProfileCompanyTypes.BuySideHedgeFund,
            UserProfileCompanyTypes.BuySideAllocator,
            UserProfileCompanyTypes.BuySideCorporate,
        ],
    },
    {
        label: "Sell-Side",
        options: [
            UserProfileCompanyTypes.SellSideAssetManagerIssuer,
            UserProfileCompanyTypes.SellSideTokenizationPlatform,
            UserProfileCompanyTypes.SellSideExchangeTradingVenue,
            UserProfileCompanyTypes.SellSideBrokerDealer,
            UserProfileCompanyTypes.SellSideMarketMaker,
        ],
    },
    {
        label: "Infrastructure",
        options: [
            UserProfileCompanyTypes.ServiceProvider,
            UserProfileCompanyTypes.BlockchainNetwork,
            UserProfileCompanyTypes.InfrastructureProvider,
        ],
    },
    {
        label: "Other",
        options: [
            UserProfileCompanyTypes.Media,
            UserProfileCompanyTypes.Academia,
            UserProfileCompanyTypes.RegulatorGovernment,
            UserProfileCompanyTypes.Other,
        ],
    },
];

function getCompanyTypeOptionLabel(option: UserProfileCompanyTypes) {
    return option.replace(/^Buy-Side - /, "").replace(/^Sell-Side - /, "");
}

const COMPANY_TYPE_OPTIONS: Array<SelectOption> = COMPANY_TYPE_GROUPS.flatMap(
    (group) =>
        group.options.map((option) => ({
            value: option,
            content: {
                label: getCompanyTypeOptionLabel(option),
                description: group.label,
            },
        })),
);

const ACQUISITION_CHANNEL_OPTIONS: Array<SelectOption> = Object.values(
    UserProfileAcquisitionChannels,
)
    .filter((channel) => channel !== UserProfileAcquisitionChannels.None)
    .map((channel) => ({
        value: channel,
        content: { label: channel },
    }));

const FORM_LABEL_CLASSNAME =
    "text-sm font-medium text-slate-700 gap-1 mb-1 flex items-center";
const FORM_CONTROL_CLASSNAME =
    "block w-full rounded-md border border-slate-300 bg-card px-3 py-2 text-sm text-slate-900 shadow-xs focus:border-slate-500 focus:outline-hidden focus:ring-1 focus:ring-slate-500 disabled:bg-slate-50 disabled:text-slate-500";
const FORM_ERROR_CLASSNAME = "mt-1 text-xs text-slate-500";
const FORM_ERROR_BORDER_CLASSNAME = "border-slate-400 focus:border-slate-500";

function RequiredAsterisk() {
    return <span className="text-red-500">*</span>;
}

/**
 * InlineOnboardingModalLayout
 * Presentation-only markup for the onboarding modal - see InlineOnboardingModal
 * for the state, validation + mutations that drive it.
 */
export function InlineOnboardingModalLayout(props: {
    show: boolean;
    values: InlineOnboardingFormValues;
    errors: InlineOnboardingFormErrors;
    showNameField: boolean;
    loading: boolean;
    onChange: (values: InlineOnboardingFormValues) => void;
    onSubmit: (e: React.FormEvent) => void;
}) {
    const { show, values, errors, showNameField, loading, onChange, onSubmit } =
        props;

    return (
        <Modal show={show} allowClose={false} size="md" allowScroll={false}>
            <div className="max-h-[80vh] overflow-y-auto rounded-md border bg-card px-8 py-16 shadow-md">
                <div className="text-center mb-6">
                    <h2 className="text-2xl font-light text-slate-900">
                        Set up your RWA.xyz profile
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                        Tell us a bit about your firm so we can tailor the
                        platform to your work.
                    </p>
                </div>

                <form onSubmit={onSubmit} className="space-y-6" noValidate>
                    {showNameField && (
                        <div>
                            <label
                                htmlFor="name"
                                className={FORM_LABEL_CLASSNAME}
                            >
                                Full name
                                <RequiredAsterisk />
                            </label>
                            <input
                                id="name"
                                name="name"
                                type="text"
                                required
                                value={values.name}
                                onChange={(e) =>
                                    onChange({
                                        ...values,
                                        name: e.target.value,
                                    })
                                }
                                className={cn(
                                    FORM_CONTROL_CLASSNAME,
                                    "placeholder-slate-400",
                                    errors.name
                                        ? FORM_ERROR_BORDER_CLASSNAME
                                        : "",
                                )}
                                placeholder="Enter your full name"
                                aria-invalid={errors.name}
                                disabled={loading}
                            />
                            {errors.name && (
                                <p className={FORM_ERROR_CLASSNAME}>
                                    Enter your full name.
                                </p>
                            )}
                        </div>
                    )}

                    <div>
                        <label
                            htmlFor="company"
                            className={FORM_LABEL_CLASSNAME}
                        >
                            Company
                            <RequiredAsterisk />
                        </label>
                        <input
                            id="company"
                            name="company"
                            type="text"
                            required
                            value={values.company}
                            onChange={(e) =>
                                onChange({
                                    ...values,
                                    company: e.target.value,
                                })
                            }
                            className={cn(
                                FORM_CONTROL_CLASSNAME,
                                "placeholder-slate-400",
                                errors.company
                                    ? FORM_ERROR_BORDER_CLASSNAME
                                    : "",
                            )}
                            placeholder="Enter your company name"
                            aria-invalid={errors.company}
                            disabled={loading}
                        />
                        {errors.company && (
                            <p className={FORM_ERROR_CLASSNAME}>
                                Enter your company name.
                            </p>
                        )}
                    </div>

                    <div>
                        <label className={FORM_LABEL_CLASSNAME}>
                            Firm type
                            <RequiredAsterisk />
                        </label>
                        <SelectInput
                            name="companyType"
                            disabled={loading}
                            value={values.companyType}
                            onChange={(updatedValue) =>
                                onChange({
                                    ...values,
                                    companyType:
                                        updatedValue as UserProfileCompanyTypes,
                                })
                            }
                            options={COMPANY_TYPE_OPTIONS}
                            placeholder="Choose your firm type"
                            size="sm"
                            optionsContainerHeight="lg"
                            className={cn("w-full", {
                                [FORM_ERROR_BORDER_CLASSNAME]:
                                    errors.companyType,
                            })}
                        />
                        {errors.companyType && (
                            <p className={FORM_ERROR_CLASSNAME}>
                                Select your firm type.
                            </p>
                        )}
                    </div>

                    <div>
                        <label className={FORM_LABEL_CLASSNAME}>
                            How did you hear about us?
                            <RequiredAsterisk />
                        </label>
                        <SelectInput
                            name="acquisitionChannel"
                            disabled={loading}
                            value={values.acquisitionChannel}
                            onChange={(updatedValue) =>
                                onChange({
                                    ...values,
                                    acquisitionChannel:
                                        updatedValue as UserProfileAcquisitionChannels,
                                })
                            }
                            options={ACQUISITION_CHANNEL_OPTIONS}
                            placeholder="Choose an option"
                            size="sm"
                            className={cn("w-full", {
                                [FORM_ERROR_BORDER_CLASSNAME]:
                                    errors.acquisitionChannel,
                            })}
                        />
                        {errors.acquisitionChannel && (
                            <p className={FORM_ERROR_CLASSNAME}>
                                Choose how you heard about us.
                            </p>
                        )}
                    </div>

                    <div className="flex items-start gap-3">
                        <Checkbox
                            id="should-receive-marketing"
                            checked={values.shouldReceiveMarketing}
                            onCheckedChange={(checked) =>
                                onChange({
                                    ...values,
                                    shouldReceiveMarketing: checked === true,
                                })
                            }
                            disabled={loading}
                            className="mt-0.5"
                        />
                        <label
                            htmlFor="should-receive-marketing"
                            className="block text-sm text-slate-700"
                        >
                            Subscribe to our market insights newsletter
                        </label>
                    </div>

                    <div>
                        <Button
                            type="submit"
                            variant="primaryLight"
                            disabled={loading}
                            size="sm"
                            className="w-full"
                        >
                            {loading
                                ? "Creating Profile..."
                                : "Continue to RWA.xyz"}
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
}
